import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowRight, User as UserIcon } from "lucide-react";
import { ALL_SERVICES, waLink } from "@/data/business";
import { useAuth } from "@/components/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIME_SLOTS = [
  "10:00 AM", "11:00 AM", "12:00 PM", "1:00 PM", "2:00 PM",
  "3:00 PM", "4:00 PM", "5:00 PM", "6:00 PM", "7:00 PM",
];

const inputCls =
  "w-full rounded-lg border border-charcoal/15 bg-white px-4 py-3 text-sm text-charcoal placeholder:text-charcoal/35 focus:border-rose focus:outline-none transition-colors";

export default function BookingForm({ initialService = "", onSubmitted, formId = "booking" }) {
  const { user, login } = useAuth();
  const [form, setForm] = useState({
    name: "", phone: "", service: initialService, date: "", time: "", message: "",
  });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (user?.name) setForm((f) => ({ ...f, name: f.name || user.name }));
  }, [user]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await fetch(`${API}/enquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(form),
      });
    } catch (err) {
      // Enquiry still continues via WhatsApp even if the API is unreachable.
    }
    const lines = [
      "Hello Glam Girl Makeup Studio, I would like to request an appointment.",
      `Name: ${form.name}`,
      `Phone: ${form.phone}`,
      form.service && `Service: ${form.service}`,
      (form.date || form.time) &&
        `Preferred: ${[form.date, form.time].filter(Boolean).join(" at ")}`,
      form.message && `Message: ${form.message}`,
    ].filter(Boolean);
    window.open(waLink(lines.join("\n")), "_blank", "noopener");
    toast.success("Enquiry sent — WhatsApp has opened so you can confirm instantly.");
    setSending(false);
    setForm({ name: user?.name || "", phone: "", service: initialService, date: "", time: "", message: "" });
    onSubmitted?.();
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid={`${formId}-form`}>
      {user ? (
        <p className="flex items-center gap-2 rounded-lg border border-rose/25 bg-rose-soft/20 px-4 py-2.5 text-xs font-medium text-rose-deep" data-testid={`${formId}-signed-in-note`}>
          <UserIcon className="h-3.5 w-3.5" />
          Signed in as {user.name} — this enquiry will be linked to your account.
        </p>
      ) : (
        <button
          type="button"
          onClick={login}
          data-testid={`${formId}-signin-prompt`}
          className="w-full rounded-lg border border-charcoal/12 bg-cream px-4 py-2.5 text-xs font-medium text-charcoal/60 transition-colors hover:border-rose/40 hover:text-rose"
        >
          Sign in with Google to pre-fill your details and track enquiries →
        </button>
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor={`${formId}-name`} className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-charcoal/60">Name</label>
          <input id={`${formId}-name`} data-testid={`${formId}-name-input`} required value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} />
        </div>
        <div>
          <label htmlFor={`${formId}-phone`} className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-charcoal/60">Phone Number</label>
          <input id={`${formId}-phone`} data-testid={`${formId}-phone-input`} required type="tel" pattern="[0-9+ ]{10,15}" value={form.phone} onChange={set("phone")} placeholder="Your phone number" className={inputCls} />
        </div>
      </div>
      <div>
        <label htmlFor={`${formId}-service`} className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-charcoal/60">Service Interested In</label>
        <select id={`${formId}-service`} data-testid={`${formId}-service-select`} value={form.service} onChange={set("service")} className={inputCls}>
          <option value="">Select a service</option>
          {ALL_SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor={`${formId}-date`} className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-charcoal/60">Preferred Date</label>
          <input id={`${formId}-date`} data-testid={`${formId}-date-input`} type="date" value={form.date} onChange={set("date")} className={inputCls} />
        </div>
        <div>
          <label htmlFor={`${formId}-time`} className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-charcoal/60">Preferred Time</label>
          <select id={`${formId}-time`} data-testid={`${formId}-time-select`} value={form.time} onChange={set("time")} className={inputCls}>
            <option value="">Select a time</option>
            {TIME_SLOTS.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label htmlFor={`${formId}-message`} className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-charcoal/60">Message</label>
        <textarea id={`${formId}-message`} data-testid={`${formId}-message-input`} rows={3} value={form.message} onChange={set("message")} placeholder="Anything we should know?" className={inputCls} />
      </div>
      <button
        type="submit"
        disabled={sending}
        data-testid={`${formId}-submit-button`}
        className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-charcoal px-8 py-4 text-sm font-semibold tracking-wide text-ivory transition-colors hover:bg-rose-deep disabled:opacity-60"
      >
        {sending ? "Sending…" : "Request Appointment"}
        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
      </button>
      <p className="text-center text-xs text-charcoal/50">
        Appointment requests are subject to salon confirmation.
      </p>
    </form>
  );
}
